import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import GoogleButton from "./googleButton";
import PasswordInput from "./passwordInput";
import { Dots } from "../Loader/loader";
import "./auth.css";

const hasArmenian = (text) => /[Ա-֏]/.test(text || "");

// Server may answer with English framework messages; show only Armenian text.
const authErrorMessage = (err) => {
  const status = err.response?.status;
  const serverText = err.response?.data?.errors?.email?.[0] || err.response?.data?.message;
  if (hasArmenian(serverText)) return serverText;
  if (status === 422) return "Լրացրեք վավեր էլ. հասցե և գաղտնաբառ։";
  if (status === 429) return "Չափից շատ փորձեր։ Խնդրում ենք փորձել մի փոքր ուշ։";
  if (!err.response) return "Կապի սխալ։ Ստուգեք ինտերնետ կապը։";
  return "Չհաջողվեց մուտք գործել։ Փորձեք կրկին։";
};

const Login = () => {
  const { login, completeTwoFactor, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [challenge, setChallenge] = useState(null); // set when the account asks for a 2FA code
  const [code, setCode] = useState("");

  if (isAuthenticated) return <Navigate to="/profile" replace />;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result = await login(form.email, form.password);
      if (result.twoFactor) setChallenge(result.challenge);
      else navigate("/profile");
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCode = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await completeTwoFactor(challenge, code);
      navigate("/profile");
    } catch (err) {
      const text = err.response?.data?.errors?.code?.[0];
      setError(
        err.response?.status === 429
          ? "Չափից շատ փորձեր։ Խնդրում ենք փորձել մի փոքր ուշ։"
          : hasArmenian(text)
          ? text
          : "Չհաջողվեց։ Փորձեք կրկին։"
      );
      // the code step is closed on the server after too many wrong codes or after the time runs out
      if (/ժամկետը/.test(text || "")) setChallenge(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (challenge) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2 className="auth-title">Երկքայլ նույնականացում</h2>
          <form className="auth-form" onSubmit={handleCode} noValidate>
            {error && <div className="auth-alert">{error}</div>}
            <p className="auth-muted" style={{ textAlign: "center" }}>
              Մուտքագրեք նույնականացման հավելվածի 6-անիշ կոդը կամ վերականգնման կոդը։
            </p>
            <div className="auth-field">
              <label htmlFor="code">Կոդ</label>
              <input
                id="code"
                name="code"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                maxLength={16}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <button className="auth-btn" type="submit" disabled={submitting || code.length < 6}>
              {submitting ? <Dots /> : "Հաստատել"}
            </button>
          </form>
          <p className="auth-switch">
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setChallenge(null);
                setCode("");
                setError("");
              }}
            >
              Վերադառնալ
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2 className="auth-title">Մուտք</h2>
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {error && <div className="auth-alert">{error}</div>}
          <div className="auth-field">
            <label htmlFor="email">Էլ. հասցե</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="auth-field">
            <label htmlFor="password">Գաղտնաբառ</label>
            <PasswordInput
              id="password"
              name="password"
              autoComplete="current-password"
              value={form.password}
              onChange={handleChange}
              required
            />
          </div>
          <Link to="/forgot-password" className="auth-forgot">
            Մոռացե՞լ եք գաղտնաբառը
          </Link>
          <button className="auth-btn" type="submit" disabled={submitting}>
            {submitting ? <Dots /> : "Մուտք գործել"}
          </button>
        </form>
        <GoogleButton
          onSuccess={() => navigate("/profile")}
          onError={setError}
          onTwoFactor={(c) => setChallenge(c)}
        />
        <p className="auth-switch">
          Դեռ հաշիվ չունե՞ք։ <Link to="/register">Գրանցվել</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
